import React, { Component, ReactNode } from 'react';
import { Button, Result } from 'antd';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

/**
 * 错误边界组件 - 专门处理代码分块加载失败
 *
 * 当 Vite 代码分块加载失败时（通常发生在部署新版本后），
 * 自动刷新页面以加载新版本的资源
 *
 * @example
 * ```tsx
 * <ChunkErrorBoundary>
 *   <YourApp />
 * </ChunkErrorBoundary>
 * ```
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // 检测是否是代码分块加载错误
    const isChunkError =
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('ChunkLoadError') ||
      error.name === 'ChunkLoadError';

    return {
      hasError: true,
      error,
      isChunkError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // 记录错误到监控服务
    console.error('ChunkErrorBoundary caught error:', error, errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // 如果是代码分块错误，自动刷新页面
    if (this.state.isChunkError) {
      console.log('Chunk load error detected, reloading page...');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // 如果提供了自定义 fallback，使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 代码分块错误的特殊处理
      if (this.state.isChunkError) {
        return (
          <Result
            status="warning"
            title="Loading New Version"
            subTitle="The application is updating, page will reload automatically..."
            extra={
              <Button type="primary" onClick={this.handleReload}>
                Reload Now
              </Button>
            }
          />
        );
      }

      // 通用错误展示
      return (
        <Result
          status="error"
          title="Something went wrong"
          subTitle={this.state.error?.message || 'An unexpected error occurred'}
          extra={
            <Button type="primary" onClick={this.handleReload}>
              Reload Page
            </Button>
          }
        />
      );
    }

    return this.props.children;
  }
}
